import { useEffect, useRef } from "react";
import type { Socket } from "socket.io-client";
import {
  addNewMessage,
  updateConversationList,
  updateCurrentChat,
} from "../../../../redux/slices/chatSlice";
import type { AppDispatch } from "../../../../redux/store";
import messageCacheDB from "../../../../services/messageCacheDB";
import { normaliseDelta } from "../../../../services/deltaSync";
import { messageGraph } from "../../../../services/messageGraph";
import { FEATURE_FLAGS } from "../../../../constants";
import { formatMessage, formatSingleChat } from "../../../../utils";
import type { ChatMessage } from "../../../../utils/computeDivider";

interface Chat {
  id: string;
  [key: string]: unknown;
}

const BOTTOM_THRESHOLD = 50;

export interface UseSocketMessagesOptions {
  socket: Socket | null;
  isConnected: boolean;
  dispatch: AppDispatch;
  selectedChatRef: React.MutableRefObject<Chat | null>;
  messagesListRef: React.MutableRefObject<ChatMessage[]>;
  messagesContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  userIdRef: React.MutableRefObject<string | null>;
  pendingScrollRef: React.MutableRefObject<"bottom" | string | null>;
  getChat: (id: string | undefined, page: number) => Promise<unknown>;
  // From useScrollEngine
  isAtBottomRef: React.MutableRefObject<boolean>;
  autoScrollRef: React.MutableRefObject<boolean>;
  shouldPinRef: React.MutableRefObject<boolean>;
  userScrolledUpRef: React.MutableRefObject<boolean>;
  // From useUnreadDivider
  unreadBadgeCountRef: React.MutableRefObject<number>;
  persistReadRef: React.MutableRefObject<(chatId: string, timestamp: string) => void>;
  setUnreadBadgeCount: React.Dispatch<React.SetStateAction<number>>;
  setDividerUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  setFirstUnreadMsgId: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useSocketMessages({
  socket,
  isConnected,
  dispatch,
  selectedChatRef,
  messagesListRef,
  messagesContainerRef,
  userIdRef,
  pendingScrollRef,
  getChat,
  isAtBottomRef,
  autoScrollRef,
  shouldPinRef,
  userScrolledUpRef,
  unreadBadgeCountRef,
  persistReadRef,
  setUnreadBadgeCount,
  setDividerUnreadCount,
  setFirstUnreadMsgId,
}: UseSocketMessagesOptions): void {
  const wasConnectedRef = useRef(isConnected);

  // Reconnect: re-fetch current chat when socket re-establishes connection.
  useEffect(() => {
    if (!isConnected || wasConnectedRef.current) {
      wasConnectedRef.current = isConnected;
      return;
    }
    wasConnectedRef.current = true;
    const chatId = selectedChatRef.current?.id;
    if (chatId) {
      pendingScrollRef.current = "bottom";
      getChat(chatId, 1);
    }
  }, [isConnected, getChat, selectedChatRef, pendingScrollRef]);

  // receive-message: handle real-time incoming messages.
  useEffect(() => {
    if (!socket) return;

    const handleReceiveMessage = (msg: Record<string, unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let rawMsg: any = (msg.newMessage as any) ?? (msg.messageList as any[])?.[0];
      if (FEATURE_FLAGS.USE_DELTA_SYNC) {
        const delta = normaliseDelta(msg);
        if (!delta?.message) return;
        rawMsg = delta.message;
      }
      if (!rawMsg) return;

      if (rawMsg?.conversationId !== selectedChatRef.current?.id) return;

      const formattedMsg = formatMessage(rawMsg, userIdRef.current);
      const isOwnEcho = !!formattedMsg.isUser;

      if (FEATURE_FLAGS.USE_CRDT_ENGINE && formattedMsg?.id) {
        if (messageGraph.has(String(formattedMsg.id))) return;
        messageGraph.mirror(rawMsg.conversationId, formattedMsg);
      }

      if (isOwnEcho) {
        const alreadyAcked = messagesListRef.current.some(
          (m) => String(m.id) === String(formattedMsg.id) && !m.isTempMsg,
        );
        if (!alreadyAcked) {
          dispatch(addNewMessage(formattedMsg));
        }
        // Own echo — ensure pin stays set (ChatFooter already called scrollToBottom).
        shouldPinRef.current = true;
        userScrolledUpRef.current = false;
        return;
      }

      // Incoming from others — deduplicate.
      if (messagesListRef.current.some((m) => m.id === formattedMsg.id)) return;

      const container = messagesContainerRef.current;
      const isActuallyAtBottom =
        !container ||
        container.scrollHeight - container.scrollTop - container.clientHeight <=
          BOTTOM_THRESHOLD;

      if (isActuallyAtBottom) {
        // User is at the bottom — auto-scroll and mark as read.
        isAtBottomRef.current = true;
        autoScrollRef.current = true;
        const chatId = selectedChatRef.current?.id;
        if (chatId && formattedMsg.createdAt) {
          persistReadRef.current(chatId, formattedMsg.createdAt);
        }
      } else {
        // User scrolled up — increment badge and show divider.
        unreadBadgeCountRef.current += 1;
        const newCount = unreadBadgeCountRef.current;
        setUnreadBadgeCount(newCount);
        setDividerUnreadCount(newCount);
        if (newCount === 1 && formattedMsg.id) {
          setFirstUnreadMsgId(String(formattedMsg.id));
        }
        if (selectedChatRef.current?.id) {
          dispatch(
            updateConversationList({
              id: selectedChatRef.current.id,
              unreadCount: newCount,
              ...(rawMsg?.ofUser !== undefined && {
                ofUser: String(rawMsg.ofUser),
              }),
            }),
          );
        }
      }

      // Enrich sender display info from chat context when socket payload omits it.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const enriched = formattedMsg as any;
      if (!enriched.senderName) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const chat = selectedChatRef.current as any;
        if (chat && !chat.isGroup && !chat.isBroadCast) {
          if (!enriched.number) enriched.number = chat.number;
          enriched.senderName = chat.savedName || chat.number;
        } else if (chat?.isGroup) {
          const member = (chat.groupMembers || []).find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (m: any) => String(m.userId ?? m._id) === String(rawMsg?.ofUser),
          );
          if (member) {
            if (!enriched.number) enriched.number = member.phoneNumber ?? member.number;
            enriched.senderName = member.name ?? member.fullName ?? member.phoneNumber ?? member.number;
          }
        }
      }

      dispatch(addNewMessage(formattedMsg));

      if (userIdRef.current && formattedMsg.id) {
        messageCacheDB
          .putMessages(userIdRef.current, rawMsg.conversationId, [formattedMsg])
          .catch(() => {});
      }
    };

    socket.on("receive-message", handleReceiveMessage);
    return () => { socket.off("receive-message", handleReceiveMessage); };
  }, [
    socket,
    dispatch,
    selectedChatRef,
    messagesListRef,
    messagesContainerRef,
    userIdRef,
    isAtBottomRef,
    autoScrollRef,
    shouldPinRef,
    userScrolledUpRef,
    unreadBadgeCountRef,
    persistReadRef,
    setUnreadBadgeCount,
    setDividerUnreadCount,
    setFirstUnreadMsgId,
  ]);

  // user-updates: sync conversation metadata changes in real time.
  useEffect(() => {
    if (!socket) return;

    const handleUserUpdates = (msg: Record<string, unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updatedConversation = formatSingleChat((msg?.conversation as any[])?.[0]);
      if (updatedConversation.id === selectedChatRef.current?.id) {
        dispatch(updateCurrentChat(updatedConversation));
        dispatch(updateConversationList(updatedConversation));
      } else if (updatedConversation?.id) {
        dispatch(updateCurrentChat(updatedConversation));
      }
    };

    socket.on("user-updates", handleUserUpdates);
    return () => { socket.off("user-updates", handleUserUpdates); };
  }, [socket, dispatch, selectedChatRef]);
}
